import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  ViewChild,
  inject,
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';

import { CountdownTypes } from '@modules/countdown/enums/countdown-types.enum';
import { MediaImageModel } from '@modules/media/models/media-image.model';
import { MediaVideoModel } from '@modules/media/models/media-video.model';
import { Screen, WindowService } from '@modules/shared/services/window.service';

import { CountdownComponent } from '../../../countdown/countdown/countdown.component';
import { MediaComponent } from '../../../media/media.component';

@Component({
  selector: 'mb-announcement-banner-small',
  templateUrl: './announcement-banner-small.component.html',
  styleUrls: ['./announcement-banner-small.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MediaComponent, NgClass, NgTemplateOutlet, CountdownComponent],
})
export class AnnouncementBannerSmallComponent implements AfterViewInit, OnDestroy {
  private cdr = inject(ChangeDetectorRef);
  private windowService = inject(WindowService);

  @ViewChild('countdownContainer') countdownContainer: ElementRef<HTMLElement> | null = null;
  @ViewChild('titleContainer') titleContainer: ElementRef<HTMLElement> | null = null;
  @ViewChild('subtitleContainer') subtitleContainer: ElementRef<HTMLElement> | null = null;

  private destroy$ = new Subject<boolean>();
  private _countdownDate: Date | null = null;

  public innerLayout = 'default';

  public isFinished = false;

  @Input() countdownType = CountdownTypes.FULL;

  @Input() title: string = '';
  @Input() subtitle: string = '';
  @Input() titleHash: string = '';
  @Input() media!: MediaImageModel | MediaVideoModel;
  @Input() fuelLabel: string = '';
  @Input()
  set countdownDate(d: Date | null) {
    this._countdownDate = d;
    if (d && (d as Date).getTime() < new Date().getTime()) {
      this.isFinished = true;
    }
  }
  get countdownDate() {
    return this._countdownDate;
  }
  @Input() countdownLabel = 'Live in';
  @Input() withCountdown!: boolean;
  @Input() isRearCountdown: boolean = false;
  @Input() timezoneOffset: string = 'GMT';

  finished() {
    this.isFinished = true;
    this.cdr.detectChanges();
  }

  checkLayout(screen: Screen) {
    if (!this.titleContainer || !this.subtitleContainer || !this.countdownContainer) return;

    let maxWidth = 1000,
      newLayout = 'default';
    if (screen >= Screen.XXL) {
      maxWidth = 1750;
    } else {
      maxWidth = screen;
    }
    if (this.titleContainer.nativeElement.clientWidth >= maxWidth) {
      newLayout = 'all-row-layout';
    } else {
      const totalWidth = [this.countdownContainer, this.titleContainer, this.subtitleContainer].reduce(
        (prev, current) => {
          return current.nativeElement.clientWidth + prev;
        },
        0
      );
      if (totalWidth > maxWidth) {
        newLayout = 'row-layout';
      }
    }
    if (newLayout !== this.innerLayout) {
      this.innerLayout = newLayout;
      this.cdr.detectChanges();
    }
  }

  ngAfterViewInit() {
    this.windowService.screen$.pipe(takeUntil(this.destroy$)).subscribe((screen) => {
      if (screen >= Screen.MD) {
        this.checkLayout(screen);
      } else {
        if (this.innerLayout !== 'default') {
          this.innerLayout = 'default';
          this.cdr.detectChanges();
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }
}
